import React from 'react';
import PropTypes from 'prop-types';

import { getFormattedDate } from 'utils';

import { statusLabel, statusTone, tierNote } from './constants';
import { rowPhotoUrl } from './photo';
import { sub, displayName, gradeAndState } from './row-fields';
import {
  awaitingAssembly,
  canPublish,
  examLabel,
  initials,
  parkedReason,
  scoreLine,
} from './queue-fields';
import {
  Avatar,
  AvatarInitials,
  AvatarImg,
  Btn,
  MonoCopy,
  NumCell,
  Pill,
  Primary,
  QueueRow,
  ReasonBody,
  ReasonRow,
  ReasonText,
  RowActions,
  Secondary,
  TierPill,
  Who,
  WhoMeta,
  WhoName,
} from './styled-comps';

const COLUMNS = 6;

const hideOnError = e => {
  e.target.style.display = 'none';
};

const QueueTableRow = ({ row, examNames, onOpen, onPublish, onCopyStudentId }) => {
  const name = displayName(row);
  const score = scoreLine(row, examNames);
  const tierChip = tierNote(row.tier);
  const reason = parkedReason(row);
  const photo = rowPhotoUrl(row);
  const { studentId } = sub(row);

  return (
    <>
      <QueueRow clickable tone={statusTone(row.status)} onClick={() => onOpen(row)}>
        <td>
          <Who>
            <Avatar aria-hidden="true">
              <AvatarInitials>{initials(name)}</AvatarInitials>
              {/* Scanned by face as much as by name, including the unassembled rows,
                  whose photo the record does not carry. */}
              {photo && <AvatarImg src={photo} alt="" onError={hideOnError} />}
            </Avatar>
            <div>
              <WhoName unnamed={!name}>{name || 'no name yet'}</WhoName>
              <WhoMeta>{gradeAndState(row)}</WhoMeta>
              {/* How an admin looks this child up while the card has no name on it. */}
              {studentId && (
                <MonoCopy
                  title={`Student ${studentId} — click to copy`}
                  onClick={event => {
                    event.stopPropagation();
                    onCopyStudentId(studentId);
                  }}
                >
                  {String(studentId).slice(0, 8)}
                </MonoCopy>
              )}
            </div>
          </Who>
        </td>
        <td>
          <Primary>{examLabel(row, examNames)}</Primary>
          {score && <Secondary>{score}</Secondary>}
        </td>
        <td>
          <Pill tone={statusTone(row.status)}>{statusLabel(row.status)}</Pill>
          {/* "Logged only" is equally true of an impact story and of a row judged not to
              belong, so outside the tile that filtered to it the distinction is lost. */}
          {tierChip && <TierPill tone={tierChip.tone}>{tierChip.label}</TierPill>}
          {awaitingAssembly(row) && <Secondary>not assembled</Secondary>}
        </td>
        <td>
          <Pill tone={row.consent ? 'granted' : 'denied'}>
            {row.consent ? 'Yes' : 'No'}
          </Pill>
        </td>
        <NumCell>
          <Secondary>
            {row.created_on ? getFormattedDate(row.created_on, 'D MMM YYYY') : '—'}
          </Secondary>
        </NumCell>
        <td>
          <RowActions onClick={event => event.stopPropagation()}>
            <Btn small onClick={() => onOpen(row)}>
              Review
            </Btn>
            {canPublish(row) && (
              <Btn small variant="publish" onClick={() => onPublish(row)}>
                Publish
              </Btn>
            )}
          </RowActions>
        </td>
      </QueueRow>
      {reason && (
        <ReasonRow>
          <td colSpan={COLUMNS}>
            <ReasonText>
              <ReasonBody aria-hidden="true">&#9888;</ReasonBody>
              <ReasonBody>
                <b>Parked — </b>
                {reason}
              </ReasonBody>
            </ReasonText>
          </td>
        </ReasonRow>
      )}
    </>
  );
};

QueueTableRow.propTypes = {
  row: PropTypes.shape({}).isRequired,
  examNames: PropTypes.shape({}).isRequired,
  onOpen: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onCopyStudentId: PropTypes.func.isRequired,
};

export default React.memo(QueueTableRow);
